// Everything the runner draws on its canvas. The canvas is rendered at a fraction
// of its on-screen size and scaled up with image-rendering: pixelated, so every
// shape here comes out as chunky pixel art without having to be drawn as such.
//
// All coordinates are in canvas pixels, not CSS pixels - view.pixel converts.

// The articles each runner can have - der/die/das, le/la. A view is made for one
// set of them (makeView), and everything here reads its lanes from that view.
export const GERMAN_ARTICLES = ["der", "die", "das"];

// The noun gender labs' colours, brightened to read against a night track: blue
// for masculine, purple for feminine, gold for neuter, in both languages.
export const LANE_COLORS = { das: "#f2c12e", der: "#4fa3f7", die: "#d47cf0", la: "#d47cf0", le: "#4fa3f7" };

export const BRAND_GREEN = "#5cb85c";

// How to show a noun with its article. French elides before a vowel - "l'accord" -
// which hides the very thing the game is about, so say which one it is.
export function withArticle(noun) {
  const elided = /^l['’]/i.test(noun.written) && noun.article !== "l'";
  return elided ? `${noun.written} (${noun.article})` : noun.written;
}

// World units. A gate spawns at FAR and the player stands at 0.
export const FAR = 30;
const DEPTH_SCALE = 4;
const TIE_SPACING = 1.2;
const PILLAR_SPACING = 3.6;

// joystix is for the chrome - score, billboards, the brand. The German itself
// (the articles and the nouns) is set in the labs serif, the same face the noun
// gender labs use: a learner should see the words in a normal typeface, and
// joystix has no ß anyway.
export const PIXEL_FONT = "joystix, 'Courier New', monospace";
export const WORD_FONT = "Georgia, 'Iowan Old Style', 'Palatino Linotype', Palatino, serif";

// Banded rather than a smooth gradient - a gradient is the first thing that
// gives away a "pixel" scene that isn't.
const SKY_BANDS = ["#160f3a", "#211650", "#341d61", "#4f236b", "#73296f", "#9c346d", "#c94c66", "#ef7158", "#ff9c5a"];

// CSS pixels per canvas pixel. Coarser on a big screen, where there's room for
// it; on a phone 3 would make the scene too coarse to read.
function pixelSize(cssWidth) {
  return cssWidth < 520 ? 2 : 3;
}

// Perspective: p(z) is 1 at the player and 0 at the horizon, so screen y and
// lane x are both linear in it and straight lines in the world stay straight.
export function makeView(cssWidth, cssHeight, articles = GERMAN_ARTICLES) {
  const pixel = pixelSize(cssWidth);
  const width = Math.max(1, Math.round(cssWidth / pixel));
  const height = Math.max(1, Math.round(cssHeight / pixel));
  const sFar = 1 / (1 + FAR / DEPTH_SCALE);
  const horizonY = Math.round(height * 0.3);
  const playerY = Math.round(height * 0.76);
  const laneWidth = Math.round(Math.min(width * 0.3, 190 / pixel));

  const p = (z) => (1 / (1 + z / DEPTH_SCALE) - sFar) / (1 - sFar);
  const y = (z) => horizonY + (playerY - horizonY) * p(z);
  // Lanes are 0..n-1, centred on the screen whatever n is.
  const lanes = articles.length;
  const middle = (lanes - 1) / 2;
  const x = (lane, z) => width / 2 + (lane - middle) * laneWidth * p(z);

  // One sprite pixel is this many canvas pixels, so the runner keeps roughly
  // half a lane's width whatever the screen.
  const sprite = Math.max(1, Math.round(laneWidth / 32));

  // Where the runner's shoes touch the track.
  const footY = Math.round(playerY + laneWidth * 0.32);

  return {
    articles, footY, height, horizonY, laneWidth, lanes, middle, p, pixel, playerY, sprite, width, x, y,
  };
}

function quad(ctx, points, fill) {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  points.slice(1).forEach(([px, py]) => ctx.lineTo(px, py));
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

// A strip of track between two lane positions, from z0 (far) to z1 (near).
function strip(ctx, view, laneA, laneB, z0, z1, fill) {
  quad(
    ctx,
    [
      [view.x(laneA, z0), view.y(z0)],
      [view.x(laneB, z0), view.y(z0)],
      [view.x(laneB, z1), view.y(z1)],
      [view.x(laneA, z1), view.y(z1)],
    ],
    fill
  );
}

function drawSky(ctx, view) {
  const bandHeight = Math.ceil(view.horizonY / SKY_BANDS.length);
  SKY_BANDS.forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, i * bandHeight, view.width, bandHeight + 1);
  });

  // Stars in the top bands, placed by index so they don't twinkle about.
  ctx.fillStyle = "#fff6d5";
  for (let i = 0; i < 28; i += 1) {
    const sx = (i * 97) % view.width;
    const sy = (i * 53) % Math.max(1, Math.round(view.horizonY * 0.45));
    ctx.fillRect(sx, sy, 1, 1);
  }

  // A low pixel sun sitting on the horizon.
  const sunRadius = Math.round(view.horizonY * 0.22);
  ctx.fillStyle = "#ffd36b";
  for (let dy = -sunRadius; dy <= 0; dy += 1) {
    const half = Math.round(Math.sqrt(sunRadius * sunRadius - dy * dy));
    ctx.fillRect(Math.round(view.width * 0.72) - half, view.horizonY + dy, half * 2, 1);
  }

  // Skyline with lit windows, fixed to the horizon.
  const blockWidth = Math.max(6, Math.round(view.width / 16));
  for (let i = 0; i * blockWidth < view.width + blockWidth; i += 1) {
    const h = Math.round(view.horizonY * (0.12 + ((i * 37) % 23) / 70));
    const left = i * blockWidth - 2;
    ctx.fillStyle = i % 2 ? "#2a1d4d" : "#22183f";
    ctx.fillRect(left, view.horizonY - h, blockWidth - 1, h);
    ctx.fillStyle = "#ffcf6e";
    for (let wy = view.horizonY - h + 2; wy < view.horizonY - 2; wy += 3) {
      for (let wx = left + 1; wx < left + blockWidth - 2; wx += 3) {
        if ((wx * 7 + wy * 13 + i) % 5 === 0) ctx.fillRect(wx, wy, 1, 1);
      }
    }
  }
}

function drawBillboard(ctx, view, z, lane, text, color, font = PIXEL_FONT) {
  const scale = view.p(z);
  const boardWidth = view.laneWidth * 1.1 * scale;
  const boardHeight = view.laneWidth * 0.32 * scale;
  const left = view.x(lane, z) - boardWidth / 2;
  const top = view.y(z) - view.laneWidth * 2.1 * scale;

  ctx.fillStyle = "#14161f";
  ctx.fillRect(left - 1, top - 1, boardWidth + 2, boardHeight + 2);
  ctx.fillStyle = color;
  ctx.fillRect(left, top, boardWidth, boardHeight);

  const size = Math.round(boardHeight * 0.5);
  if (size < 5) return;

  ctx.font = font === PIXEL_FONT ? `${size}px ${font}` : `700 ${size}px ${font}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#14161f";
  ctx.fillText(text, left + boardWidth / 2, top + boardHeight / 2 + 1, boardWidth * 0.9);
}

export function drawBackdrop(ctx, view, game) {
  const { width, height, horizonY } = view;

  drawSky(ctx, view);

  ctx.fillStyle = "#1d2029";
  ctx.fillRect(0, horizonY, width, height - horizonY);

  // Ballast under the track, then a wash of each lane's colour so the lanes read
  // as der, die and das all the way to the horizon.
  strip(ctx, view, -0.55, view.lanes - 0.45, FAR, -3, "#3d3631");
  view.articles.forEach((article, lane) => {
    strip(ctx, view, lane - 0.47, lane + 0.47, FAR, -3, `${LANE_COLORS[article]}26`);
  });

  // Sleepers scroll toward you, which is most of the sense of speed.
  const shift = game.distance % TIE_SPACING;
  ctx.fillStyle = "#5e4c3c";
  for (let z = FAR - shift; z > -3; z -= TIE_SPACING) {
    const thickness = Math.max(1, Math.round(3 * view.p(z)));
    for (let lane = 0; lane < view.lanes; lane += 1) {
      const left = Math.round(view.x(lane - 0.38, z));
      const right = Math.round(view.x(lane + 0.38, z));
      ctx.fillRect(left, Math.round(view.y(z)), right - left, thickness);
    }
  }

  // Rails
  ctx.strokeStyle = "#b9c1cc";
  ctx.lineWidth = 1;
  for (let lane = 0; lane < view.lanes; lane += 1) {
    [-0.25, 0.25].forEach((offset) => {
      ctx.beginPath();
      ctx.moveTo(view.x(lane + offset, FAR), view.y(FAR));
      ctx.lineTo(view.x(lane + offset, -3), view.y(-3));
      ctx.stroke();
    });
  }

  // Pillars either side, far to near so nearer ones overlap. Every other pair
  // carries a billboard - which is where the branding lives.
  const pillarShift = game.distance % PILLAR_SPACING;
  const firstIndex = Math.floor(game.distance / PILLAR_SPACING);
  for (let z = FAR - pillarShift; z > -1; z -= PILLAR_SPACING) {
    const scale = view.p(z);
    const pillarHeight = view.laneWidth * 2.4 * scale;
    const pillarWidth = Math.max(2, Math.round(view.laneWidth * 0.16 * scale));
    [-1.1, view.lanes + 0.1].forEach((lane) => {
      const px = Math.round(view.x(lane, z));
      ctx.fillStyle = "#454a57";
      ctx.fillRect(px - pillarWidth / 2, view.y(z) - pillarHeight, pillarWidth, pillarHeight);
      ctx.fillStyle = "#ffd27a";
      ctx.fillRect(px - pillarWidth / 2, view.y(z) - pillarHeight, pillarWidth, Math.max(1, Math.round(2 * scale)));
    });

    // Numbered from the start of the run, so a billboard keeps its text as it
    // scrolls rather than swapping every time a pillar leaves the screen.
    const pillarNumber = firstIndex + Math.round((FAR - pillarShift - z) / PILLAR_SPACING);
    if (pillarNumber % 2 === 0) {
      drawBillboard(ctx, view, z, -1.1, "CLOZEMASTER", BRAND_GREEN);
      const article = view.articles[(pillarNumber / 2) % view.lanes];
      drawBillboard(ctx, view, z, view.lanes + 0.1, article, LANE_COLORS[article], WORD_FONT);
    }
  }
}

// --- The articles ---------------------------------------------------------
//
// der, die and das are the one thing on screen you have to read at speed, so
// they're not pixel art: they're drawn on a second, full-resolution canvas laid
// over the pixel one. Its transform maps canvas pixels to device pixels, so the
// coordinates below are the same as everywhere else here - the text just comes
// out smooth.

// The article painted on the ground at the bottom, so you can always see which
// lane is which without looking away from the track.
function drawLaneLabels(ctx, view, game) {
  // Fitted into the strip between the runner's feet and the bottom edge,
  // rather than placed at a depth - at a fixed depth the serif ran off the
  // bottom of the screen.
  const top = view.footY + view.sprite * 2;
  const room = Math.max(4, view.height - top);
  const size = Math.min(view.laneWidth * 0.36, room * 0.8);
  const y = top + room / 2;
  // Lane x at that row, so the labels still follow the perspective.
  const p = (y - view.horizonY) / (view.playerY - view.horizonY);
  const laneX = (lane) => view.width / 2 + (lane - view.middle) * view.laneWidth * p;

  ctx.font = `700 ${size}px ${WORD_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  view.articles.forEach((article, lane) => {
    ctx.globalAlpha = lane === game.lane ? 1 : 0.5;
    ctx.fillStyle = "#14161f";
    ctx.fillText(article, laneX(lane) + 1, y + 1);
    ctx.fillStyle = LANE_COLORS[article];
    ctx.fillText(article, laneX(lane), y);
  });
  ctx.globalAlpha = 1;
}

export function drawGate(ctx, view, gate) {
  if (gate.z > FAR || gate.z < -1.5) return;

  const scale = view.p(gate.z);
  const base = Math.round(view.y(gate.z));
  const panelHeight = Math.round(view.laneWidth * 0.75 * scale);
  const answer = view.articles.indexOf(gate.noun.article);
  const border = Math.max(1, Math.round(2 * scale));

  view.articles.forEach((article, lane) => {
    // A passed gate's right panel is the one you smashed through - skip it.
    if (gate.result === "correct" && lane === answer && gate.z < 0.2) return;

    const left = Math.round(view.x(lane - 0.44, gate.z));
    const right = Math.round(view.x(lane + 0.44, gate.z));
    let fill = "#161822";
    let edge = LANE_COLORS[article];

    if (gate.result) {
      if (lane === answer) {
        fill = "#2ea043";
        edge = "#7ee787";
      } else if (lane === gate.chosen) {
        fill = "#c62828";
        edge = "#ff9a9a";
      } else {
        fill = "#161822";
        edge = "#3a3f4d";
      }
    }

    // Square panels with a hard border: a rounded, anti-aliased rectangle is
    // the other thing that breaks the pixel look.
    ctx.fillStyle = edge;
    ctx.fillRect(left, base - panelHeight, right - left, panelHeight);
    ctx.fillStyle = fill;
    ctx.fillRect(left + border, base - panelHeight + border, right - left - border * 2, panelHeight - border * 2);

  });
}

// Too small to read at the horizon, in CSS pixels.
const MIN_ARTICLE_SIZE = 7;

function drawGateArticles(ctx, view, gate, game) {
  if (gate.z > FAR || gate.z < -1.5) return;

  const scale = view.p(gate.z);
  const size = view.laneWidth * 0.3 * scale;
  if (size * view.pixel < MIN_ARTICLE_SIZE) return;

  const base = Math.round(view.y(gate.z));
  const panelHeight = Math.round(view.laneWidth * 0.75 * scale);
  const answer = view.articles.indexOf(gate.noun.article);

  ctx.font = `700 ${size}px ${WORD_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  view.articles.forEach((article, lane) => {
    if (gate.result === "correct" && lane === answer && gate.z < 0.2) return;
    // The runner is drawn over the panel in their own lane as it reaches them,
    // and this canvas is on top of everything - so leave that one out rather
    // than float it over the runner.
    if (lane === game.lane && gate.z < 0.6) return;

    ctx.fillStyle = gate.result && lane === answer ? "#fff" : LANE_COLORS[article];
    ctx.fillText(article, view.x(lane, gate.z), base - panelHeight / 2 + 1);
  });
}

export function drawArticles(ctx, view, game) {
  drawLaneLabels(ctx, view, game);
  game.gates
    .slice()
    .sort((a, b) => b.z - a.z)
    .forEach((gate) => drawGateArticles(ctx, view, gate, game));
}

// --- The runner -----------------------------------------------------------
//
// Seen from behind, like the game this borrows from: backwards Clozemaster
// green cap, green hoodie with a white C. 16 sprite pixels wide; the arms and
// legs are separate so they can swing.

const PALETTE = {
  B: "#2f4a7a",
  C: BRAND_GREEN,
  F: "#f4f4f4",
  G: BRAND_GREEN,
  H: "#6b3e1f",
  K: "#14161f",
  S: "#f1c27d",
  W: "#ffffff",
  b: "#223559",
  c: "#3f8f3f",
  g: "#3f8f3f",
  s: "#d9a066",
};

const BODY = [
  ".....KKKKKK.....",
  "....KCCCCCCK....",
  "...KCCCCCCCCK...",
  "...KCCCCCCCCK...",
  "...KccccccccK...",
  "...KHHHHHHHHK...",
  "..KsHHHHHHHHsK..",
  "...KHHHHHHHHK...",
  "....KKSSSSKK....",
  "..KKGGGGGGGGKK..",
  ".KGGGGGGGGGGGGK.",
  ".KGGGGWWWWGGGGK.",
  ".KGGGWGGGGGGGGK.",
  ".KGGGWGGGGGGGGK.",
  ".KGGGGWWWWGGGGK.",
  ".KgGGGGGGGGGGgK.",
  "..KggggggggggK..",
  "..KBBBBBBBBBBK..",
];

const ARM = ["KK", "GK", "GK", "gK", "SK", "KK"];

function drawPixels(ctx, rows, originX, originY, unit, mirror) {
  rows.forEach((row, ry) => {
    for (let rx = 0; rx < row.length; rx += 1) {
      const key = row[mirror ? row.length - 1 - rx : rx];
      if (key !== ".") {
        ctx.fillStyle = PALETTE[key];
        ctx.fillRect(originX + rx * unit, originY + ry * unit, unit, unit);
      }
    }
  });
}

// A leg of `length` sprite pixels: denim, then a white trainer and its sole.
function legRows(length) {
  const rows = [];
  for (let i = 0; i < length - 2; i += 1) rows.push(i === length - 3 ? "KbbK" : "KBBK");
  rows.push("KFFK", "KKKK");
  return rows;
}

// Four-frame run cycle: [left leg, right leg, body bob]. The leg that's
// forward reads as shorter from behind, since its foot is in the air.
const RUN_FRAMES = [
  [6, 4, 0],
  [5, 5, -1],
  [4, 6, 0],
  [5, 5, -1],
];

// How long the dive lasts on screen: the dash itself plus the landing.
const DIVE_SECONDS = 0.38;

// Streaks rushing past toward the horizon while dashing.
export function drawSpeedLines(ctx, view, game) {
  const sinceDash = game.time - (game.lastDashAt ?? -10);
  if (sinceDash < 0 || sinceDash > DIVE_SECONDS) return;

  ctx.globalAlpha = 0.55 * (1 - sinceDash / DIVE_SECONDS);
  ctx.fillStyle = "#ffffff";
  for (let i = 0; i < 14; i += 1) {
    // Scattered across the width by index, sliding toward the camera over time.
    const lane = -1.4 + ((i * 0.37) % 1) * (view.lanes + 1.8);
    const near = ((game.time * 9 + i * 0.29) % 1) * 3;
    const z0 = near + 2.5;
    const x0 = Math.round(view.x(lane, z0));
    const x1 = Math.round(view.x(lane, near));
    const y0 = Math.round(view.y(z0));
    const y1 = Math.round(view.y(near));
    const steps = Math.max(1, Math.abs(y1 - y0));
    for (let t = 0; t <= steps; t += 1) {
      ctx.fillRect(Math.round(x0 + ((x1 - x0) * t) / steps), y0 + Math.sign(y1 - y0) * t, 1, 1);
    }
  }
  ctx.globalAlpha = 1;
}

export function drawRunner(ctx, view, game) {
  const unit = view.sprite;
  const cx = Math.round(view.x(game.laneX, 0));
  const { footY } = view;
  const crashed = game.paused;

  // A hop after a right answer, a stagger after a crash.
  const sinceHop = game.time - (game.hopAt || -10);
  let hop = sinceHop < 0.35 ? Math.round(Math.sin((sinceHop / 0.35) * Math.PI) * 6 * unit) : 0;

  // A dash is a long, low dive: higher than a hop, legs tucked, and it lands a
  // beat after the gate so you see it arrive.
  const sinceDash = game.time - (game.lastDashAt ?? -10);
  const diving = sinceDash >= 0 && sinceDash < DIVE_SECONDS;
  if (diving) hop = Math.max(hop, Math.round(Math.sin((sinceDash / DIVE_SECONDS) * Math.PI) * 10 * unit));

  // Math.abs: % keeps the sign in JS, so a negative clock would index past the
  // frames rather than wrapping.
  const frameIndex = Math.abs(Math.floor(game.time * 12)) % RUN_FRAMES.length;
  let frame = crashed ? [5, 5, 0] : RUN_FRAMES[frameIndex];
  if (diving && !crashed) frame = [4, 4, 0];
  const [leftLeg, rightLeg, bob] = frame;
  const bodyRows = BODY.length;
  const height = (bodyRows + 6) * unit;
  const top = footY - height + bob * unit - hop;
  const left = cx - 8 * unit;

  // Shadow stays on the ground through a hop.
  ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
  const shadowWidth = 12 * unit - Math.round(hop / 3);
  ctx.fillRect(cx - shadowWidth / 2, footY - unit, shadowWidth, unit * 2);

  ctx.save();

  // Lean into a lane change, in whole pixels of shear rather than a rotation.
  const lean = Math.round((game.lane - game.laneX) * 3) * unit;
  if (crashed) ctx.globalAlpha = Math.floor(game.time * 10) % 2 ? 1 : 0.55;

  const legTop = top + bodyRows * unit;
  drawPixels(ctx, legRows(leftLeg), left + 3 * unit, legTop, unit, false);
  drawPixels(ctx, legRows(rightLeg), left + 9 * unit, legTop, unit, false);
  drawPixels(ctx, BODY, left + lean, top, unit, false);

  // Arms swing against the legs; after a crash they go up in the air.
  const swing = leftLeg > rightLeg ? 1 : leftLeg < rightLeg ? -1 : 0;
  const armTop = top + 9 * unit;
  const leftArmY = crashed ? top + 4 * unit : armTop - swing * unit;
  const rightArmY = crashed ? top + 4 * unit : armTop + swing * unit;
  drawPixels(ctx, ARM, left + lean - unit, leftArmY, unit, true);
  drawPixels(ctx, ARM, left + lean + 15 * unit, rightArmY, unit, false);

  ctx.restore();

  // Dizzy stars circling the cap after a crash.
  if (crashed) {
    ctx.fillStyle = "#ffe066";
    for (let i = 0; i < 3; i += 1) {
      const angle = game.time * 6 + (i * Math.PI * 2) / 3;
      const sx = Math.round(cx + Math.cos(angle) * 7 * unit);
      const sy = Math.round(top - 2 * unit + Math.sin(angle) * 2 * unit);
      ctx.fillRect(sx, sy, unit * 2, unit * 2);
    }
  }
}

// --- Particles ------------------------------------------------------------

export function drawParticles(ctx, game, dt) {
  game.particles = game.particles.filter((particle) => particle.life > 0);
  game.particles.forEach((particle) => {
    particle.life -= dt;
    particle.x += particle.vx * dt;
    particle.y += particle.vy * dt;
    particle.vy += particle.gravity * dt;
    ctx.globalAlpha = Math.max(0, particle.life / particle.maxLife);
    ctx.fillStyle = particle.color;
    ctx.fillRect(Math.round(particle.x), Math.round(particle.y), particle.size, particle.size);
  });
  ctx.globalAlpha = 1;
}

// Shards of the panel you smashed through.
export function burst(game, view, lane, color) {
  const cx = view.x(lane, 0);
  const cy = view.playerY - view.laneWidth * 0.3;
  const speedScale = view.laneWidth * 3;
  for (let i = 0; i < 26; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = speedScale * (0.4 + Math.random());
    const life = 0.5 + Math.random() * 0.5;
    game.particles.push({
      color: i % 3 === 0 ? "#ffe066" : color,
      gravity: speedScale * 3,
      life,
      maxLife: life,
      size: Math.max(1, Math.round(view.sprite * (1 + Math.random() * 2))),
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - speedScale * 0.8,
      x: cx,
      y: cy,
    });
  }
}

// A puff kicked up from the feet on a lane change.
export function dust(game, view) {
  const cx = view.x(game.laneX, 0);
  const cy = view.playerY + view.laneWidth * 0.3;
  for (let i = 0; i < 6; i += 1) {
    const life = 0.25 + Math.random() * 0.2;
    game.particles.push({
      color: "#8a8f9c",
      gravity: 0,
      life,
      maxLife: life,
      size: view.sprite,
      vx: (Math.random() - 0.5) * view.laneWidth * 1.5,
      vy: -Math.random() * view.laneWidth * 0.6,
      x: cx + (Math.random() - 0.5) * view.laneWidth * 0.3,
      y: cy,
    });
  }
}
